/**
 * AddCardStepNav - Step breadcrumb, back button and card heading for the Add to Collection modal
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoArrowBack, IoChevronForward } from 'react-icons/io5';

import type { MinimalCard } from '@/services/cardDatabase';

import type { AddCardPhase } from '../hooks/useAddToCollectionFlow';

export interface AddCardStepNavProps {
  phase: AddCardPhase;
  availablePhases: AddCardPhase[];
  canVisit: (phase: AddCardPhase) => boolean;
  onNavigate: (phase: AddCardPhase) => void;
  /** Phase the back button returns to; null hides it */
  previousPhase: AddCardPhase | null;
  onBack: () => void;
  card: MinimalCard | null;
  printing: MinimalCard | null;
  /** Full set name for the printing, when known */
  printingSetName?: string;
}

const PHASE_LABELS: Record<AddCardPhase, string> = {
  search: 'Card',
  edition: 'Edition',
  details: 'Details',
};

const BACK_LABELS: Record<AddCardPhase, string> = {
  search: 'Back to search',
  edition: 'Back to editions',
  details: 'Back to details',
};

export default function AddCardStepNav({
  phase,
  availablePhases,
  canVisit,
  onNavigate,
  previousPhase,
  onBack,
  card,
  printing,
  printingSetName,
}: AddCardStepNavProps): ReactElement {
  const showHeading = phase !== 'search' && card;

  return (
    <div className="mb-4 flex flex-col gap-3">
      {availablePhases.length > 1 && (
        <nav aria-label="Add card steps">
          <ol className="flex items-center gap-1 text-xs">
            {availablePhases.map((step, index) => {
              const isCurrent = step === phase;
              const isEnabled = !isCurrent && canVisit(step);
              return (
                <li key={step} className="flex items-center gap-1">
                  {index > 0 && <IoChevronForward className="text-ash/60 h-3 w-3" aria-hidden="true" />}
                  <button
                    type="button"
                    onClick={() => onNavigate(step)}
                    disabled={!isEnabled}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={clsx(
                      'focus-ring flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium transition-colors',
                      isCurrent && 'bg-aether/20 text-pearl',
                      isEnabled && 'text-silver hover:bg-slate hover:text-pearl',
                      !isCurrent && !isEnabled && 'text-ash/60 cursor-default',
                    )}
                  >
                    <span
                      className={clsx(
                        'flex h-4 w-4 items-center justify-center rounded-full text-[10px]',
                        isCurrent ? 'bg-aether text-void' : 'bg-storm text-silver',
                      )}
                    >
                      {index + 1}
                    </span>
                    {PHASE_LABELS[step]}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {showHeading && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            {previousPhase && (
              <button
                type="button"
                onClick={onBack}
                className="text-ash hover:text-pearl bg-slate hover:bg-storm focus-ring mt-0.5 rounded-lg p-2 transition-colors"
                aria-label={BACK_LABELS[previousPhase]}
                title={BACK_LABELS[previousPhase]}
              >
                <IoArrowBack className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-pearl text-lg font-semibold">{card.name}</h3>
              <p className="text-ash text-sm">{card.type_line}</p>
            </div>
          </div>
          {phase === 'details' && printing && (
            <div className="min-w-0 text-right">
              <p className="text-silver font-mono text-sm uppercase">
                {printing.set} <span className="text-ash">#{printing.collector_number}</span>
              </p>
              {printingSetName && <p className="text-ash max-w-56 truncate text-xs">{printingSetName}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
